import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { submitQuestionLink } from "../../redux/slices/SendQuestionsSlice.js";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Dropdown from "./Dropdown.jsx";
import { FaRegStar } from "react-icons/fa";
import { apiEndpointsMap, PLATFORM_OPTIONS } from "./constants.js";

const AddQuestions = () => {
  const navigate = useNavigate();
  const userData = useSelector((state) => state.user.user);
  const modules = Object.keys(apiEndpointsMap);

  //Redirect user if they're not Admin
  if (!userData.isAdmin) {
    navigate("/");
  }

  const dispatch = useDispatch();

  const [quesData, setQuesData] = useState({});

  const handleQuesData = () => {
    // On Submit
    const payload = {
      title: quesData.title?.trim(),
      problemLink: quesData.problemLink?.trim(),
      platform: quesData.platform?.trim(),
      rating: quesData.rating,
      apiEndpoint: apiEndpointsMap[quesData.apiEndpoint],
      solutionVideo: quesData.solutionVideo?.trim() || "",
      hints: quesData.hints?.trim() || "",
    };
    dispatch(submitQuestionLink(payload))
      .unwrap()
      .then(() => {
        toast.success("Question added successfully!");
        navigate("/admin");
      })
      .catch((err) => {
        toast.error("Failed to add question. Please try again.");
        console.error("Submission error:", err);
      });
  };

  const validateLink = (e) => {
    const link = e.target.value;
    const isValidUrl =
      /^(https?:\/\/)([\w.-]+)\.([a-z.]{2,6})(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/.test(
        link
      );

    if (link && !isValidUrl) {
      toast.warning("Please enter a valid link");
    }
  };

  const onTitleChange = (e) => {
    const title = e.target.value;
    setQuesData((prev) => ({
      ...prev,
      title: title,
    }));
  };

  const onLinkChange = (e) => {
    const link = e.target.value;
    setQuesData((prev) => ({
      ...prev,
      problemLink: link,
    }));
  };

  const onModuleSelect = (value) => {
    setQuesData((prev) => ({
      ...prev,
      apiEndpoint: value,
    }));
  };

  const onRatingChange = (e) => {
    const rating = e.target.value;
    // Validate rating input
    if (rating && !/^\d+$/.test(rating)) return;

    setQuesData({
      ...quesData,
      rating: e.target.value,
    });
  };

  const onPlatformSelect = (value) => {
    setQuesData((prev) => ({
      ...prev,
      platform: value,
    }));
  };

  const onSolutionVideoChange = (e) => {
    const videoLink = e.target.value;
    setQuesData((prev) => ({
      ...prev,
      solutionVideo: videoLink,
    }));
  };

  const onHintsChange = (e) => {
    const hints = e.target.value;
    setQuesData((prev) => ({
      ...prev,
      hints: hints,
    }));
  };

  const isSubmitDisabled = () => {
    return (
      !quesData.title ||
      !quesData.problemLink ||
      !quesData.apiEndpoint ||
      !quesData.platform ||
      !quesData.rating ||
      isNaN(quesData.rating) ||
      quesData.rating < 0
    );
  };

  return (
    <div className="h-screen flex items-center flex-col justify-center w-full bg-blue2 dark:bg-dark">
      <h1 className="text-blue dark:bg-dark  bg-blue2 text-center font-bold text-4xl md:text-4xl dark:text-white ">
        Add Question
      </h1>
      <p className="text-base text-white mt-2">
        Please provide the question details including the title, link, platform,
        difficulty rating, associated module, solution video, and hints.
      </p>
      <div className="mt-[20px] flex flex-col items-center justify-center w-full">
        <div className="flex flex-col justify-center items-center w-4/5 gap-4">
          {/* Question Title Input */}
          <input
            type="text"
            onChange={onTitleChange}
            value={quesData.title || ""}
            className={`w-full text-sm font-medium p-2 rounded-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            name="title"
            placeholder="Enter question title (e.g., Two Sum, Binary Search Problem)"
            title="Please enter a descriptive title for the question"
          />

          <div className="flex justify-between gap-2 items-center w-full">
            <input
              type="url"
              onChange={onLinkChange}
              value={quesData.problemLink || ""}
              className={`w-full text-sm font-medium p-2 rounded-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500`}
              name="questions"
              placeholder="Enter question link (e.g., https://example.com)"
              pattern="https?://.+"
              onBlur={validateLink}
            />

            <input
              type="url"
              onChange={onSolutionVideoChange}
              value={quesData.solutionVideo || ""}
              className={`w-full text-sm font-medium p-2 rounded-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500`}
              name="solutionVideo"
              placeholder="Enter solution video link (e.g., https://youtube.com/watch?v=...)"
              pattern="https?://.+"
              onBlur={validateLink}
            />
          </div>

          {/* Hints Textarea */}
          <textarea
            onChange={onHintsChange}
            value={quesData.hints || ""}
            className={`w-full text-sm font-medium p-2 rounded-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical min-h-[100px]`}
            name="hints"
            rows="1"
            placeholder="Enter hints for solving the problem (e.g., Use dynamic programming, Consider edge cases, Think about time complexity...)"
            title="Provide helpful hints for solving this problem"
          />
        </div>

        <div className="flex gap-2 w-full justify-center mt-4">
          <Dropdown
            topics={modules}
            onModuleSelect={onModuleSelect}
            value={quesData.apiEndpoint}
            variant="MODULE_SELECTOR"
          />

          <div className="w-1/4 flex items-center justify-start px-4 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-sm cursor-pointer hover:border-blue-400 transition-colors">
            <FaRegStar size={20} color="gray" />
            <input
              placeholder="Rating (e.g., 800, 1000, 1200)"
              className="p-2 bg-transparent flex-1 outline-none text-white"
              onChange={onRatingChange}
              value={quesData.rating || ""}
            />
          </div>

          <Dropdown
            topics={PLATFORM_OPTIONS}
            onModuleSelect={onPlatformSelect}
            value={quesData.platform}
            variant="PLATFORM_SELECTOR"
          />
        </div>
        <button
          onClick={handleQuesData}
          disabled={isSubmitDisabled()}
          className={`font-medium flex items-center justify-center border-2 rounded-sm py-2.5 px-10 mt-10 mx-auto w-[20%]
    text-white ease-in-out duration-300
    ${
      isSubmitDisabled()
        ? "bg-gray-400 border-gray-400 cursor-not-allowed opacity-60"
        : "bg-blue6 border-blue6 hover:text-white hover:border-black hover:bg-black dark:bg-blue dark:border-blue dark:hover:bg-dark dark:hover:text-blue"
    }
  `}
        >
          Submit Question
        </button>
      </div>
    </div>
  );
};

export default AddQuestions;
